import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import meetingService from '../../services/meetingService';
import { notify } from '../CustomToast';
import './MyTimeProposalsModal.css';
import { useI18n } from '../../i18n';
import ProposalListSkeleton from './ProposalListSkeleton';

const MyTimeProposalsModal = ({ meeting, onClose }) => {
  const { t } = useI18n();
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProposals();
  }, [meeting?.id]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await meetingService.listMyProposals(meeting.id);

      if (response.status === true && response.data && response.data.proposals) {
        const proposals = response.data.proposals;
        setProposals(proposals);
        if (proposals.length > 0) {
          setSelectedProposal(proposals[0]);
        }
      }
    } catch (error) {
      notify('error', t('common.messages.error_loading_proposals') || 'Error loading proposals');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'accepted':
        return 'mtpm-status-accepted';
      case 'rejected':
        return 'mtpm-status-rejected';
      case 'pending':
      default:
        return 'mtpm-status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted':
        return t('pages.meetings.status_accepted') || 'Accepted';
      case 'rejected':
        return t('pages.meetings.status_rejected') || 'Rejected';
      case 'pending':
      default:
        return t('pages.meetings.status_pending') || 'Pending';
    }
  };

  const content = (
    <div className="mtpm-overlay" onClick={onClose}>
      <div className="mtpm-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Gradient Header */}
        <div className="mtpm-header">
          <h2 className="mtpm-title">{t('pages.meetings.my_time_proposals') || 'My Time Proposals'}</h2>
          <button className="mtpm-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="mtpm-body">
          {/* Left Panel - Proposal List */}
          <div className="mtpm-left-panel">
            <div className="mtpm-list">
              {loading ? (
                <ProposalListSkeleton />
              ) : proposals.length === 0 ? (
                <div className="mtpm-empty">{t('pages.meetings.no_proposals_submitted') || 'You haven\'t submitted any time proposals for this meeting.'}</div>
              ) : (
                proposals.map(proposal => (
                  <div
                    key={proposal.id}
                    className={`mtpm-proposal-item ${selectedProposal?.id === proposal.id ? 'selected' : ''}`}
                    onClick={() => setSelectedProposal(proposal)}
                  >
                    <div className="mtpm-proposal-header">
                      <span className="mtpm-proposal-time">
                        {formatDateTime(proposal.proposed_start_at)}
                      </span>
                      <span className={`mtpm-status-badge ${getStatusBadgeClass(proposal.status)}`}>
                        {getStatusLabel(proposal.status)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Details */}
          <div className="mtpm-right-panel">
            {selectedProposal ? (
              <>
                {/* Time Comparison */}
                <div className="mtpm-section">
                  <h3>{t('pages.meetings.time_comparison') || 'Time Comparison'}</h3>

                  <div className="mtpm-time-block">
                    <div className="mtpm-time-label">{t('pages.meetings.current_time') || 'Current Time'}</div>
                    <div className="mtpm-time-value">
                      {formatDateTime(selectedProposal.current_meeting_start_at)} - {formatDateTime(selectedProposal.current_meeting_end_at)}
                    </div>
                  </div>

                  <div className="mtpm-time-block">
                    <div className="mtpm-time-label">{t('pages.meetings.proposed_time') || 'Proposed Time'}</div>
                    <div className="mtpm-time-value">
                      {formatDateTime(selectedProposal.proposed_start_at)} - {formatDateTime(selectedProposal.proposed_end_at)}
                    </div>
                  </div>
                </div>

                {/* Message */}
                {selectedProposal.message && (
                  <div className="mtpm-section">
                    <h3>{t('common.message') || 'Message'}</h3>
                    <div className="mtpm-message">{selectedProposal.message}</div>
                  </div>
                )}

                {/* Status */}
                <div className="mtpm-section">
                  <h3>{t('pages.meetings.status') || 'Status'}</h3>
                  <div className={`mtpm-status-card mtpm-status-${selectedProposal.status}`}>
                    {getStatusLabel(selectedProposal.status)}
                  </div>
                </div>
              </>
            ) : (
              <div className="mtpm-no-selection">
                {t('pages.meetings.select_proposal') || 'Select a proposal to view details'}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mtpm-footer">
          <button className="mtpm-btn-close" onClick={onClose}>
            {t('common.actions.close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default MyTimeProposalsModal;
